import axiosInstance from '@/Api/axiosV2Config';
import type {
  AssistanceArticle,
  AssistanceFormula,
  AssistancePurchasePrice,
  PurchasePriceCell,
  AssistanceResolvedPrice,
  AssistanceSocietyPrice,
} from '../types/assistance.types';

export const assistanceApi = {
  /** Référentiel des formules proposables, triées par ordre d'affichage. */
  getFormulas: async (): Promise<AssistanceFormula[]> => {
    const { data } = await axiosInstance.get<AssistanceFormula[]>('/assistance/formulas');
    return data;
  },

  /**
   * Tarifs résolus de toutes les formules pour une société et une durée.
   * Renvoie une liste vide sur une durée non éligible.
   */
  getPrices: async (
    societyId: string,
    duration: number,
  ): Promise<AssistanceResolvedPrice[]> => {
    const { data } = await axiosInstance.get<{ duration: number; prices: AssistanceResolvedPrice[] }>(
      `/assistance/societies/${societyId}/prices`,
      { params: { duration } },
    );
    return data.prices;
  },

  /** Grille des prix d'achat Mutuaide, par case. Réservée aux internes (403 sinon). */
  getPurchasePriceGrid: async (): Promise<PurchasePriceCell[]> => {
    const { data } = await axiosInstance.get<PurchasePriceCell[]>('/assistance/purchase-prices');
    return data;
  },

  /** Programme un nouveau prix d'achat, à une date d'effet immédiate ou future. */
  schedulePurchasePrice: async (input: {
    formulaId: number;
    duration: number;
    price: number;
    effectiveDate: string;
  }): Promise<AssistancePurchasePrice> => {
    const { data } = await axiosInstance.post<AssistancePurchasePrice>('/assistance/purchase-prices', input);
    return data;
  },

  /** Annule un prix d'achat programmé, pas encore entré en vigueur. */
  deletePurchasePrice: async (id: number): Promise<AssistancePurchasePrice> => {
    const { data } = await axiosInstance.delete<AssistancePurchasePrice>(`/assistance/purchase-prices/${id}`);
    return data;
  },

  /** Texte de l'encart d'assistance des CP. `null` si la migration n'est pas jouée. */
  getArticle: async (): Promise<AssistanceArticle | null> => {
    const { data } = await axiosInstance.get<AssistanceArticle | null>('/assistance/article');
    return data;
  },

  /** Corrige ce texte. Réservé aux internes côté API. */
  updateArticle: async (
    changes: Partial<Pick<AssistanceArticle, 'title' | 'intro' | 'footer' | 'phone' | 'email' | 'isVisible'>>,
  ): Promise<AssistanceArticle> => {
    const { data } = await axiosInstance.patch<AssistanceArticle>('/assistance/article', changes);
    return data;
  },

  /**
   * Matrice des tarifs résolus d'une société : chaque couple (formule, durée) avec
   * son prix et la source qui l'a fourni.
   */
  getSocietyPricing: async (societyId: string): Promise<AssistanceSocietyPrice[]> => {
    const { data } = await axiosInstance.get<AssistanceSocietyPrice[]>(
      `/assistance/societies/${societyId}/resolved-pricing`,
    );
    return data;
  },

  /** Enregistre un tarif négocié. Sans effet sur les garanties existantes. */
  createCustomPrice: async (
    societyId: string,
    payload: { formulaId: number; duration: number; price: number },
  ): Promise<void> => {
    await axiosInstance.post(`/assistance/societies/${societyId}/custom-pricing`, payload);
  },

  /** Supprime un tarif négocié : retour au parent facturant ou au catalogue. */
  deleteCustomPrice: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/assistance/custom-pricing/${id}`);
  },
};
